import {
  ProviderDefinition,
  ProviderFieldDefinition,
} from "../services/api";

export const resolveProviderModelValue = (
  definition: ProviderDefinition,
  settings: Record<string, any>
): string | undefined => {
  const modelField = definition.fields.find((field) => field.name === "model");
  if (!modelField) {
    return undefined;
  }

  const provided = settings.model ?? modelField.default;
  if (provided === undefined || provided === null || provided === "") {
    return undefined;
  }

  const candidate = String(provided);
  if (modelField.options?.length) {
    const allowed = new Set(modelField.options.map((option) => option.value));
    if (!allowed.has(candidate)) {
      return modelField.default !== undefined
        ? String(modelField.default)
        : undefined;
    }
  }

  return candidate;
};

export const getEffectiveProviderField = (
  field: ProviderFieldDefinition,
  modelValue: string | undefined
): ProviderFieldDefinition => {
  if (!modelValue || !field.model_overrides) {
    return field;
  }

  const modelOverride = field.model_overrides[modelValue];
  if (!modelOverride) {
    return field;
  }

  return {
    ...field,
    ...modelOverride,
  };
};

export const isProviderFieldVisibleForModel = (
  field: ProviderFieldDefinition,
  modelValue: string | undefined
): boolean => {
  if (!field.show_for_models?.length) {
    return true;
  }
  if (!modelValue) {
    return false;
  }
  return field.show_for_models.includes(modelValue);
};

export const getEffectiveProviderFields = (
  definition: ProviderDefinition,
  settings: Record<string, any>
) => {
  const modelValue = resolveProviderModelValue(definition, settings);
  const fields = definition.fields
    .map((field) => getEffectiveProviderField(field, modelValue))
    .filter((field) => isProviderFieldVisibleForModel(field, modelValue));

  return { fields, modelValue };
};

export const sanitizeProviderSettings = (
  definition: ProviderDefinition,
  settings: Record<string, any>
): Record<string, any> => {
  const knownFields = new Set(definition.fields.map((field) => field.name));
  const sanitized: Record<string, any> = {};

  Object.entries(settings).forEach(([key, value]) => {
    if (!knownFields.has(key)) {
      sanitized[key] = value;
    }
  });

  const { fields } = getEffectiveProviderFields(definition, settings);
  fields.forEach((field) => {
    const key = field.name;
    const providedValue = settings[key];
    let nextValue = providedValue;

    if (nextValue === undefined || nextValue === null || nextValue === "") {
      if (field.default !== undefined) {
        sanitized[key] = field.default;
      }
      return;
    }

    if (field.type === "select" && field.options?.length) {
      const allowed = new Set(field.options.map((option) => String(option.value)));
      if (!allowed.has(String(nextValue))) {
        if (field.default !== undefined) {
          sanitized[key] = field.default;
        }
        return;
      }
    }

    if (field.type === "number" || field.type === "range") {
      const numeric = typeof nextValue === "number" ? nextValue : Number(nextValue);
      const inRange =
        !Number.isNaN(numeric) &&
        (field.min === undefined || numeric >= field.min) &&
        (field.max === undefined || numeric <= field.max);

      if (!inRange) {
        if (field.default !== undefined) {
          sanitized[key] = field.default;
        }
        return;
      }

      nextValue = numeric;
    }

    if (field.type === "toggle") {
      nextValue = Boolean(nextValue);
    }

    sanitized[key] = nextValue;
  });

  return sanitized;
};

export const buildDefaultProviderSettings = (
  definition: ProviderDefinition
): Record<string, any> => sanitizeProviderSettings(definition, {});
